import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import HeightRoundedIcon from '@mui/icons-material/HeightRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SosRoundedIcon from '@mui/icons-material/SosRounded';
import AddRoadRoundedIcon from '@mui/icons-material/AddRoadRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded';
import ElectricBoltRoundedIcon from '@mui/icons-material/ElectricBoltRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import PageLayout from '../components/PageLayout';
import Section, { SectionHeading } from '../components/PageSection';
import {
  FeatureCard,
  FeatureIcon,
  CardTitle,
  CardText,
  DarkCard,
  CheckItem,
  accentAt,
  AccreditationNote,
} from '../components/ui';

const SITE = 'https://qasc.it';

// Formazione obbligatoria dei lavoratori (art. 37 D.Lgs. 81/08).
const baseCourses = [
  {
    icon: <GroupsRoundedIcon />,
    title: 'Formazione generale — 4 ore',
    text: 'La formazione di base sulla sicurezza per tutti i lavoratori impiegati in tutte le aziende, erogabile anche in modalità e-learning. Costituisce credito formativo permanente; per i neoassunti il percorso va completato entro 60 giorni dall’assunzione.',
  },
  {
    icon: <HealthAndSafetyRoundedIcon />,
    title: 'Formazione specifica — 4, 8 o 12 ore',
    text: 'L’approfondimento in funzione del settore di appartenenza dell’azienda secondo i codici ATECO 2007: rischio basso 4 ore (anche in e-learning), rischio medio 8 ore, rischio alto 12 ore. Aggiornamento obbligatorio quinquennale di 6 ore per tutti i settori (anche in e-learning).',
  },
];

// Corsi per figure specializzate.
const roleCourses = [
  {
    icon: <SupervisorAccountRoundedIcon />,
    title: 'Preposti — 8 ore',
    text: 'La formazione particolare aggiuntiva per chi sovrintende il lavoro di altri soggetti coordinati (capo cantiere, capo squadra, capo turno), secondo l’Accordo Stato-Regioni in vigore dal 26 gennaio 2012. Aggiornamento biennale di 6 ore.',
  },
  {
    icon: <BusinessCenterRoundedIcon />,
    title: 'Dirigenti — 16 ore',
    text: 'Il percorso per la figura incaricata dal Datore di Lavoro di organizzare l’attività lavorativa dei dipendenti controllandone lo svolgimento, dettagliato nell’Accordo Stato-Regioni del 21/12/2011. Aggiornamento quinquennale di 6 ore.',
  },
  {
    icon: <RecordVoiceOverRoundedIcon />,
    title: 'RLS — 32 ore',
    text: 'La formazione del Rappresentante dei lavoratori per la sicurezza, nominato ai sensi dell’art. 47 del D.Lgs. 81/08. Aggiornamento annuale: minimo 4 ore fino a 50 lavoratori, 8 ore oltre i 50.',
  },
  {
    icon: <HealthAndSafetyRoundedIcon />,
    title: 'Datore di lavoro RSPP — 16/32/48 ore',
    text: 'Per il Datore di Lavoro che svolge direttamente il ruolo di RSPP (art. 34 D.Lgs. 81/08): 16 ore per rischio basso, 32 per medio, 48 per alto. Aggiornamento quinquennale di 6, 10 o 14 ore in base al rischio.',
  },
  {
    icon: <EngineeringRoundedIcon />,
    title: 'RSPP / ASPP — moduli A, B e C',
    text: 'Il percorso dell’Accordo Stato-Regioni del 07/07/2016: modulo A di base (28 ore), modulo B comune (48 ore) con moduli di specializzazione BSP1–BSP4 per i settori che li richiedono, modulo C per i soli RSPP (24 ore). Aggiornamento nel quinquennio: 40 ore RSPP, 20 ore ASPP.',
  },
  {
    icon: <ConstructionRoundedIcon />,
    title: 'CSP / CSE — 120 ore',
    text: 'Il corso per Coordinatore della sicurezza in fase di progettazione ed esecuzione nei cantieri temporanei o mobili: 28 ore di modulo giuridico e 92 ore di modulo tecnico, metodologico-organizzativo e pratico con visita in cantiere. Aggiornamento di 40 ore nel quinquennio.',
  },
];

// Gestione emergenza.
const emergencyCourses = [
  {
    icon: <LocalFireDepartmentRoundedIcon />,
    title: 'Addetti antincendio — livelli 1, 2 e 3',
    text: 'Secondo il D.M. 02/09/2021, in vigore dal 4 ottobre 2022, le attività si dividono in Livello I, II e III: 4, 8 o 16 ore comprensive di prova pratica. Aggiornamento quinquennale di 2, 5 o 8 ore.',
  },
  {
    icon: <MedicalServicesRoundedIcon />,
    title: 'Addetti al primo soccorso — 12/16 ore',
    text: 'Ai sensi del D.M. 388/03 e dell’art. 45 del D.Lgs. 81/08: 16 ore per le aziende del gruppo A, 12 ore per i gruppi B e C, con modulo pratico di esercitazione e simulazione di emergenza. Aggiornamento triennale di 6 o 4 ore.',
  },
  {
    icon: <MonitorHeartRoundedIcon />,
    title: 'BLS-D — utilizzo del defibrillatore DAE',
    text: 'Le tecniche di base a supporto delle funzioni vitali con il defibrillatore semiautomatico esterno: massaggio cardiaco e disostruzione delle vie aeree. Almeno 5 ore tra teoria e pratica; aggiornamento biennale di almeno 3 ore.',
  },
];

// Addestramento per lavori specifici.
const specialCourses = [
  {
    icon: <HeightRoundedIcon />,
    title: 'Lavori in quota e DPI di 3ª categoria — 8 ore',
    text: 'Per chi opera ad altezza superiore a 2 metri (art. 107 D.Lgs. 81/08), con l’addestramento pratico sui DPI di 3ª categoria e i dispositivi di arresto caduta previsto dall’art. 77. Aggiornamento almeno quinquennale di 4 ore.',
  },
  {
    icon: <Inventory2RoundedIcon />,
    title: 'Ambienti e spazi confinati — 8 ore',
    text: 'Per le attività in ambienti sospetti di inquinamento o confinati, riservate a imprese e lavoratori qualificati; il corso si svolge contestualmente all’addestramento DPI di 3ª categoria, con parte teorica e pratica. Aggiornamento almeno quinquennale di 4 ore.',
  },
  {
    icon: <SosRoundedIcon />,
    title: 'Squadre di recupero e soccorso — 16 ore',
    text: 'Addestramento al recupero e soccorso di infortunati da spazi confinati e ponteggi; prerequisito: abilitazioni per spazi confinati e lavori in quota. Aggiornamento almeno quinquennale di 8 ore.',
  },
  {
    icon: <AddRoadRoundedIcon />,
    title: 'Segnaletica stradale — 8/12 ore',
    text: 'Per operatori (3 moduli, 8 ore) e preposti (3 moduli, 12 ore) impiegati in cantieri con traffico veicolare, con esercitazioni pratiche (D.Interm. 04/03/2013). Aggiornamento almeno quinquennale di 4 ore.',
  },
  {
    icon: <PrecisionManufacturingRoundedIcon />,
    title: 'Carroponte — 4/8 ore',
    text: 'Formazione obbligatoria ai sensi degli artt. 37 e 73 del D.Lgs. 81/2008; contenuti e durata definiti dal Datore di Lavoro in funzione delle specificità aziendali, con parte teorica e soprattutto pratica (fino a 12/16 ore per attrezzature particolari).',
  },
  {
    icon: <HandymanRoundedIcon />,
    title: 'Ponteggi — 28 ore',
    text: 'Il corso abilitante per montaggio, uso, smontaggio e trasformazione dei ponteggi (art. 136 e allegato XXI del Testo Unico): modulo giuridico-normativo 4 ore, tecnico 10 ore, pratico 14 ore. Aggiornamento almeno quadriennale di 4 ore.',
  },
  {
    icon: <HandymanRoundedIcon />,
    title: 'Trabattelli — 8 ore',
    text: 'Modulo integrativo di formazione e addestramento per chi utilizza, monta e trasforma il trabattello, riservato a chi ha già frequentato il corso per lavori in quota con sistemi anticaduta. Aggiornamento almeno quinquennale di 4 ore.',
  },
  {
    icon: <ElectricBoltRoundedIcon />,
    title: 'Lavori elettrici PES-PAV-PEI — 16 ore',
    text: 'Per addetti ai lavori elettrici fuori tensione, in prossimità e sotto tensione, secondo la norma CEI 11-27 (V ed. 2021) e l’art. 82 del D.Lgs. 81/08: il Datore di Lavoro attribuisce per iscritto la qualifica PES, PAV o PEI. Aggiornamento almeno quinquennale di 4 ore.',
  },
  {
    icon: <RestaurantRoundedIcon />,
    title: 'HACCP — addetti e responsabili',
    text: 'L’abilitazione a lavorare con gli alimenti, obbligatoria dal 2005 dopo l’abrogazione del libretto sanitario: 4 ore per gli addetti, 16 per i responsabili dei piani di autocontrollo. Aggiornamento almeno triennale di 4 ore.',
  },
];

// Attrezzature — Accordo Stato-Regioni 2012 (modulo teorico + pratico).
const equipment = [
  'Piattaforme di lavoro elevabili (PLE) — teoria 4 ore + pratica 4/6 ore',
  'Gru su autocarro — teoria 4 ore + pratica 8 ore',
  'Gru a torre — teoria 8 ore + pratica 4/6 ore',
  'Carrelli elevatori semoventi — teoria 8 ore + pratica 4/8 ore',
  'Gru mobili autocarrate e semoventi — teoria 7 ore + pratica 7 ore',
  'Modulo aggiuntivo falcone telescopico — teoria 4 ore + pratica 4 ore',
  'Trattori agricoli e forestali — teoria 3 ore + pratica 5 ore',
  'Escavatori, pale caricatrici, terne e autoribaltabili — teoria 4 ore + pratica 6/12 ore',
  'Pompe di calcestruzzo — teoria 7 ore + pratica 7 ore',
];

function FormazioneSicurezzaPage() {
  return (
    <PageLayout
      seo={{
        title: 'Formazione Sicurezza — Corsi D.Lgs. 81/08 | QAS Consulting',
        description:
          'Corsi di formazione sulla sicurezza sul lavoro: formazione generale e specifica dei lavoratori, preposti, dirigenti, RLS, Datore di lavoro RSPP, RSPP/ASPP, CSP/CSE, antincendio, primo soccorso, BLS-D, lavori in quota, spazi confinati, ponteggi, PES-PAV-PEI, HACCP e attrezzature.',
        canonical: `${SITE}/formazione/formazione-sicurezza`,
      }}
      hero={{
        kicker: 'Formazione · Sicurezza',
        title: 'Formazione Sicurezza',
        subtitle:
          'La formazione dei lavoratori sulla sicurezza è uno dei principali obblighi a carico del Datore di Lavoro, così come previsto in diversi articoli del D.Lgs. 81/08.',
      }}
    >
      {/* (1) Formazione obbligatoria lavoratori */}
      <Section tone="light">
        <Container maxWidth="lg">
          <SectionHeading
            kicker="Formazione obbligatoria lavoratori"
            title="Formazione generale e specifica dei lavoratori"
            subtitle="L’art. 37 del D.Lgs. 81/08 prevede che il Datore di Lavoro provveda alla formazione dei lavoratori secondo i contenuti e la durata previsti dall’Accordo Stato-Regioni del 21/12/2011. Il corso si suddivide in due moduli."
          />
          <Grid container spacing={4}>
            {baseCourses.map((m, i) => (
              <Grid item xs={12} md={6} key={m.title}>
                <FeatureCard>
                  <FeatureIcon accent={accentAt(i)}>{m.icon}</FeatureIcon>
                  <CardTitle>{m.title}</CardTitle>
                  <CardText>{m.text}</CardText>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* (2) Figure specializzate */}
      <Section tone="mist">
        <Container maxWidth="lg">
          <SectionHeading
            kicker="Corsi per figure specializzate"
            title="Le figure della sicurezza aziendale"
            subtitle="Preposti, dirigenti, rappresentanti dei lavoratori, responsabili e addetti del servizio di prevenzione e protezione, coordinatori di cantiere: ogni figura ha il suo percorso e i suoi aggiornamenti obbligatori."
          />
          <Grid container spacing={4}>
            {roleCourses.map((m, i) => (
              <Grid item xs={12} sm={6} md={4} key={m.title}>
                <FeatureCard>
                  <FeatureIcon accent={accentAt(i)}>{m.icon}</FeatureIcon>
                  <CardTitle>{m.title}</CardTitle>
                  <CardText>{m.text}</CardText>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* (3) Gestione emergenza */}
      <Section tone="dark">
        <Container maxWidth="lg">
          <SectionHeading
            tone="dark"
            kicker="Gestione emergenza"
            title="Antincendio, primo soccorso e BLS-D"
            subtitle="I corsi per i lavoratori incaricati dell’attuazione delle misure di prevenzione incendi, lotta antincendio e gestione delle emergenze, designati dal Datore di Lavoro ai sensi dell’art. 43 del D.Lgs. 81/08."
          />
          <Grid container spacing={4}>
            {emergencyCourses.map((s) => (
              <Grid item xs={12} md={4} key={s.title}>
                <DarkCard>
                  <FeatureIcon sx={{ backgroundColor: 'rgba(143,193,214,0.14)', color: '#8fc1d6' }}>
                    {s.icon}
                  </FeatureIcon>
                  <Typography
                    sx={{
                      fontFamily: '"Sora", sans-serif',
                      fontSize: '17px',
                      fontWeight: 700,
                      color: '#ffffff',
                      mb: 1.25,
                    }}
                  >
                    {s.title}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '14.5px', lineHeight: 1.7, color: 'rgba(255,255,255,0.75)' }}
                  >
                    {s.text}
                  </Typography>
                </DarkCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* (4) Addestramento lavori specifici */}
      <Section tone="light">
        <Container maxWidth="lg">
          <SectionHeading
            kicker="Addestramento per lavori specifici"
            title="Quota, spazi confinati, cantieri e altro"
            subtitle="Percorsi teorico-pratici per le attività a rischio specifico, con l’addestramento richiesto dalla normativa e gli aggiornamenti periodici."
          />
          <Grid container spacing={4}>
            {specialCourses.map((m, i) => (
              <Grid item xs={12} sm={6} md={4} key={m.title}>
                <FeatureCard>
                  <FeatureIcon accent={accentAt(i)}>{m.icon}</FeatureIcon>
                  <CardTitle>{m.title}</CardTitle>
                  <CardText>{m.text}</CardText>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* (5) Attrezzature */}
      <Section tone="mist">
        <Container maxWidth="lg">
          <SectionHeading
            kicker="Attrezzature — Accordo Stato-Regioni 2012"
            title="Le abilitazioni all’utilizzo delle attrezzature"
            subtitle="L’Accordo Stato-Regioni sulle attrezzature ha introdotto l’obbligo di partecipazione a corsi specifici per alcune attrezzature, stabilendone durata, contenuti e requisiti. Resta comunque l’obbligo per il Datore di Lavoro di formare i lavoratori anche sulle attrezzature non contemplate dall’accordo."
          />
          <Grid container spacing={2.5}>
            {equipment.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item}>
                <CheckItem>{item}</CheckItem>
              </Grid>
            ))}
          </Grid>
          <AccreditationNote />
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography
              component={RouterLink}
              to="/contatti"
              sx={{
                fontFamily: '"Sora", sans-serif',
                fontWeight: 600,
                fontSize: '15.5px',
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Contattaci per creare un corso progettato su misura in base alle esigenze formative
              della tua azienda →
            </Typography>
          </Box>
        </Container>
      </Section>
    </PageLayout>
  );
}

export default FormazioneSicurezzaPage;
